import { ARRIVE_EPSILON, INTERACT_RANGE } from "./constants.js";
import { clearsSolidFootprint, isWalkable, type MapData } from "./map.js";

export interface Point {
  x: number;
  y: number;
}

/** Tile under a continuous (tile-centered) pose. */
export function tileOf(pos: Point): Point {
  return { x: Math.round(pos.x), y: Math.round(pos.y) };
}

export function isWalkablePos(map: MapData, x: number, y: number): boolean {
  if (!isWalkable(map, Math.round(x), Math.round(y))) {
    return false;
  }
  return clearsSolidFootprint(map, x, y);
}

/** Closest walkable pose near `pos`, or null if none within the search radius. */
export function nearestWalkablePos(map: MapData, pos: Point, maxRadius = 8): Point | null {
  if (isWalkablePos(map, pos.x, pos.y)) {
    return { x: pos.x, y: pos.y };
  }
  let best: Point | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  const cx = Math.round(pos.x);
  const cy = Math.round(pos.y);
  for (let r = 1; r <= maxRadius; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) {
          continue;
        }
        const x = cx + dx;
        const y = cy + dy;
        if (!isWalkable(map, x, y)) {
          continue;
        }
        const dist = Math.hypot(pos.x - x, pos.y - y);
        if (dist < bestDist) {
          bestDist = dist;
          best = { x, y };
        }
      }
    }
    if (best) {
      return best;
    }
  }
  return null;
}

export function distance(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.hypot(dx, dy);
}

/**
 * Build a continuous polyline from `from` to exact `to`.
 * Tile A* over the baked walkability grid (nav for the park): routes around
 * blocked benches, lamps, carts, trees, etc., then ends on the free-form goal.
 */
export function buildContinuousPath(map: MapData, from: Point, to: Point): Point[] {
  if (!isWalkablePos(map, to.x, to.y)) {
    return [];
  }

  let start = tileOf(from);
  if (!isWalkable(map, start.x, start.y)) {
    const snapped = nearestWalkablePos(map, from);
    if (!snapped) {
      return [];
    }
    start = tileOf(snapped);
    from = snapped;
  }

  const goal = tileOf(to);
  if (!isWalkable(map, goal.x, goal.y)) {
    return [];
  }

  if (distance(from, to) <= ARRIVE_EPSILON) {
    return [];
  }

  // Same tile (or already adjacent with clear line): go straight.
  if (start.x === goal.x && start.y === goal.y) {
    return [{ x: to.x, y: to.y }];
  }

  const tiles = findPath(map, start, goal);
  if (tiles.length === 0) {
    return [];
  }

  const waypoints: Point[] = [];
  for (let i = 1; i < tiles.length - 1; i++) {
    const step = tiles[i]!;
    waypoints.push({ x: step.x, y: step.y });
  }
  waypoints.push({ x: to.x, y: to.y });
  return simplifyWaypoints(map, from, waypoints);
}

/** Drop intermediate points that aren't needed for line-of-sight. */
function simplifyWaypoints(map: MapData, from: Point, waypoints: Point[]): Point[] {
  if (waypoints.length <= 1) {
    return waypoints;
  }
  const out: Point[] = [];
  let cursor = from;
  let i = 0;
  while (i < waypoints.length) {
    let farthest = i;
    for (let j = i; j < waypoints.length; j++) {
      if (hasLineOfSight(map, cursor, waypoints[j]!)) {
        farthest = j;
      } else {
        break;
      }
    }
    const next = waypoints[farthest]!;
    out.push(next);
    cursor = next;
    i = farthest + 1;
  }
  return out;
}

/** Coarse LOS: sample along the segment; every sample must be walkable. */
export function hasLineOfSight(map: MapData, a: Point, b: Point): boolean {
  const dist = distance(a, b);
  const steps = Math.max(1, Math.ceil(dist * 4));
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const x = a.x + (b.x - a.x) * t;
    const y = a.y + (b.y - a.y) * t;
    if (!isWalkablePos(map, x, y)) {
      return false;
    }
  }
  return true;
}

/** Continuous approach point within interact range (stand a little off the object). */
export function nearestInteractPoint(
  map: MapData,
  from: Point,
  target: Point,
  range = INTERACT_RANGE,
): Point | null {
  if (distance(from, target) <= range && isWalkablePos(map, from.x, from.y)) {
    return { x: from.x, y: from.y };
  }

  const ideal = Math.min(range * 0.7, 1.65);
  let best: Point | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  const ring = Math.max(1, Math.ceil(range));

  for (let dy = -ring; dy <= ring; dy++) {
    for (let dx = -ring; dx <= ring; dx++) {
      if (dx === 0 && dy === 0) {
        continue;
      }
      const tileX = Math.round(target.x) + dx;
      const tileY = Math.round(target.y) + dy;
      if (!isWalkable(map, tileX, tileY)) {
        continue;
      }
      const len = Math.hypot(dx, dy) || 1;
      let approach = {
        x: target.x + (dx / len) * ideal,
        y: target.y + (dy / len) * ideal,
      };
      if (!isWalkablePos(map, approach.x, approach.y)) {
        approach = { x: tileX + 0.5, y: tileY + 0.5 };
        if (!isWalkablePos(map, approach.x, approach.y)) {
          continue;
        }
      }
      const distToTarget = distance(approach, target);
      if (distToTarget > range) {
        continue;
      }
      const score = distance(from, approach) + Math.abs(distToTarget - ideal) * 0.4;
      if (score < bestScore) {
        bestScore = score;
        best = approach;
      }
    }
  }

  return best;
}

const CARDINAL: Point[] = [
  { x: 1, y: 0 },
  { x: -1, y: 0 },
  { x: 0, y: 1 },
  { x: 0, y: -1 },
];

const DIAGONAL: Point[] = [
  { x: 1, y: 1 },
  { x: 1, y: -1 },
  { x: -1, y: 1 },
  { x: -1, y: -1 },
];

function key(width: number, x: number, y: number): number {
  return y * width + x;
}

function heuristic(a: Point, b: Point): number {
  const dx = Math.abs(a.x - b.x);
  const dy = Math.abs(a.y - b.y);
  return Math.max(dx, dy) + Math.min(dx, dy) * 0.001;
}

/**
 * Eight-direction A* with no corner-cutting. Returns a path including the
 * start tile; empty array if the goal is unreachable.
 * Uses a binary heap so long routes (e.g. around the lake) stay responsive.
 */
export function findPath(map: MapData, start: Point, goal: Point): Point[] {
  const width = map.width;
  const height = map.height;
  if (!isWalkable(map, goal.x, goal.y) || !isWalkable(map, start.x, start.y)) {
    return [];
  }
  if (start.x === goal.x && start.y === goal.y) {
    return [{ x: start.x, y: start.y }];
  }

  const startKey = key(width, start.x, start.y);
  const goalKey = key(width, goal.x, goal.y);
  const size = width * height;
  const gScore = new Float64Array(size);
  gScore.fill(Number.POSITIVE_INFINITY);
  const cameFrom = new Int32Array(size);
  cameFrom.fill(-1);
  const closed = new Uint8Array(size);

  const heapKeys: number[] = [];
  const heapF: number[] = [];

  const heapPush = (k: number, f: number): void => {
    heapKeys.push(k);
    heapF.push(f);
    let i = heapKeys.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (heapF[p]! <= heapF[i]!) {
        break;
      }
      const tk = heapKeys[p]!;
      const tf = heapF[p]!;
      heapKeys[p] = heapKeys[i]!;
      heapF[p] = heapF[i]!;
      heapKeys[i] = tk;
      heapF[i] = tf;
      i = p;
    }
  };

  const heapPop = (): number => {
    const root = heapKeys[0]!;
    const lastK = heapKeys.pop()!;
    const lastF = heapF.pop()!;
    if (heapKeys.length === 0) {
      return root;
    }
    heapKeys[0] = lastK;
    heapF[0] = lastF;
    let i = 0;
    for (;;) {
      const l = i * 2 + 1;
      const r = l + 1;
      let smallest = i;
      if (l < heapKeys.length && heapF[l]! < heapF[smallest]!) {
        smallest = l;
      }
      if (r < heapKeys.length && heapF[r]! < heapF[smallest]!) {
        smallest = r;
      }
      if (smallest === i) {
        break;
      }
      const tk = heapKeys[smallest]!;
      const tf = heapF[smallest]!;
      heapKeys[smallest] = heapKeys[i]!;
      heapF[smallest] = heapF[i]!;
      heapKeys[i] = tk;
      heapF[i] = tf;
      i = smallest;
    }
    return root;
  };

  gScore[startKey] = 0;
  heapPush(startKey, heuristic(start, goal));

  const neighbors: Point[] = [];

  while (heapKeys.length > 0) {
    const current = heapPop();
    if (closed[current]) {
      continue;
    }
    closed[current] = 1;
    if (current === goalKey) {
      return reconstructPacked(cameFrom, current, width);
    }

    const cx = current % width;
    const cy = Math.floor(current / width);
    const gHere = gScore[current]!;

    neighbors.length = 0;
    for (const d of CARDINAL) {
      const nx = cx + d.x;
      const ny = cy + d.y;
      if (nx >= 0 && ny >= 0 && nx < width && ny < height && isWalkable(map, nx, ny)) {
        neighbors.push({ x: nx, y: ny });
      }
    }
    for (const d of DIAGONAL) {
      const nx = cx + d.x;
      const ny = cy + d.y;
      if (nx < 0 || ny < 0 || nx >= width || ny >= height) {
        continue;
      }
      if (!isWalkable(map, nx, ny)) {
        continue;
      }
      if (!isWalkable(map, cx + d.x, cy) || !isWalkable(map, cx, cy + d.y)) {
        continue;
      }
      neighbors.push({ x: nx, y: ny });
    }

    for (const n of neighbors) {
      const nk = key(width, n.x, n.y);
      if (closed[nk]) {
        continue;
      }
      const step = n.x !== cx && n.y !== cy ? 1.414 : 1;
      const tentative = gHere + step;
      if (tentative >= gScore[nk]!) {
        continue;
      }
      cameFrom[nk] = current;
      gScore[nk] = tentative;
      heapPush(nk, tentative + heuristic(n, goal));
    }
  }

  return [];
}

function reconstructPacked(cameFrom: Int32Array, current: number, width: number): Point[] {
  const path: Point[] = [];
  let cursor = current;
  while (cursor >= 0) {
    path.push({
      x: cursor % width,
      y: Math.floor(cursor / width),
    });
    cursor = cameFrom[cursor]!;
  }
  path.reverse();
  return path;
}

export function chebyshev(a: Point, b: Point): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/** Closest walkable tile from which `from` can interact with `target`. */
export function nearestInteractTile(
  map: MapData,
  from: Point,
  target: Point,
): Point | null {
  if (chebyshev(from, target) <= 1 && isWalkable(map, from.x, from.y)) {
    return from;
  }

  let best: Point | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) {
        continue;
      }
      const x = target.x + dx;
      const y = target.y + dy;
      if (!isWalkable(map, x, y)) {
        continue;
      }
      const dist = Math.abs(from.x - x) + Math.abs(from.y - y);
      if (dist < bestScore) {
        bestScore = dist;
        best = { x, y };
      }
    }
  }

  return best;
}
